import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UserPlus, Edit, Trash2, Loader2, Shield, ShieldCheck, UserCog, RefreshCw } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMISSIONS_CONFIG = [
  { key: "franchisees", label: "Franchisees", description: "Manage franchisee accounts" },
  { key: "products", label: "Products", description: "Manage product catalog" },
  { key: "inventory", label: "Inventory", description: "Manage stock & inventory" },
  { key: "sales_settings", label: "Sales Settings", description: "Configure sales URL" },
];

export default function AdminUsersTab({ adminUsers, isLoading, onRefresh, currentUser }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: "", name: "", username: "", password: "",
    permissions: {
      franchisees: true,
      products: true,
      inventory: true,
      sales_settings: true
    }
  });

  const openAddDialog = () => {
    setEditingAdmin(null);
    setFormData({
      email: "", name: "", username: "", password: "",
      permissions: {
        franchisees: true,
        products: true,
        inventory: true,
        sales_settings: true
      }
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (e) => {
    const userId = e.currentTarget.getAttribute('data-user-id');
    const admin = adminUsers.find(a => a.user_id === userId);
    if (!admin) {
      toast.error("Could not find admin");
      return;
    }
    setEditingAdmin(userId);
    setFormData({
      email: admin.email,
      name: admin.name,
      username: admin.username || "",
      password: "",
      permissions: {
        franchisees: admin.permissions?.franchisees ?? true,
        products: admin.permissions?.products ?? true,
        inventory: admin.permissions?.inventory ?? true,
        sales_settings: admin.permissions?.sales_settings ?? true
      }
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.name || !formData.username) {
      toast.error("Email, name, and username are required");
      return;
    }
    if (!editingAdmin && !formData.password) {
      toast.error("Password is required for new admin");
      return;
    }
    setIsSubmitting(true);
    try {
      if (editingAdmin) {
        const updateData = {
          email: formData.email,
          name: formData.name,
          username: formData.username,
          permissions: formData.permissions
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(`${API}/admin/admins/${editingAdmin}`, updateData);
        toast.success("Admin user updated");
      } else {
        await axios.post(`${API}/admin/admins`, formData);
        toast.success("Admin user created");
      }
      setIsDialogOpen(false);
      setEditingAdmin(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save admin user");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Deactivate this admin user?")) return;
    try {
      await axios.delete(`${API}/admin/admins/${userId}`);
      toast.success("Admin user deactivated");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate");
    }
  };

  const handleReactivate = async (userId) => {
    try {
      await axios.post(`${API}/admin/admins/${userId}/reactivate`);
      toast.success("Admin user reactivated");
      onRefresh();
    } catch (error) {
      toast.error("Failed to reactivate");
    }
  };

  return (
    <>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 mb-6">
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Admins</p>
                <p className="text-2xl lg:text-3xl font-heading mt-1">{adminUsers.length}</p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 lg:w-6 lg:h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl lg:text-3xl font-heading mt-1 text-green-600">
                  {adminUsers.filter(a => a.is_active).length}
                </p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6 flex items-center justify-center">
            <Button onClick={openAddDialog} className="rounded-full" data-testid="add-admin-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Admin User
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-card overflow-hidden" data-testid="admin-users-table-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg lg:text-xl">Admin Users</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0 sm:p-6">
          {isLoading ? (
            <div className="flex justify-center py-8"><div className="spinner"></div></div>
          ) : adminUsers.length === 0 ? (
            <div className="text-center py-8">
              <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No admin users yet</p>
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <Table className="min-w-[600px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adminUsers.map((admin) => (
                    <TableRow key={admin.user_id} data-testid={`admin-row-${admin.user_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-sm">{admin.name}</p>
                          {admin.is_super_admin && (
                            <Badge className="bg-purple-500 text-white text-xs">Super</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{admin.username}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[180px]">{admin.email}</p>
                      </TableCell>
                      <TableCell>
                        {admin.is_super_admin ? (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">All Access</Badge>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {admin.permissions?.franchisees && <Badge variant="outline" className="text-xs">Franchisees</Badge>}
                            {admin.permissions?.products && <Badge variant="outline" className="text-xs">Products</Badge>}
                            {admin.permissions?.inventory && <Badge variant="outline" className="text-xs">Inventory</Badge>}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`rounded-full text-xs ${admin.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                          {admin.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {admin.is_super_admin ? (
                          <span className="text-xs text-muted-foreground">Protected</span>
                        ) : admin.is_active ? (
                          <>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              data-user-id={admin.user_id}
                              onClick={openEditDialog}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="text-destructive" 
                              onClick={() => handleDelete(admin.user_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleReactivate(admin.user_id)}
                            className="text-green-600"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Reactivate
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingAdmin ? "Edit Admin User" : "Add New Admin User"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                Admin users can access the admin panel with specific permissions.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  data-testid="admin-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter full name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  data-testid="admin-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="admin@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Username *</Label>
                <Input
                  data-testid="admin-username-input"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="username"
                />
              </div>
              <div className="space-y-2">
                <Label>Password {editingAdmin ? "(leave blank to keep)" : "*"}</Label>
                <Input
                  type="password"
                  data-testid="admin-password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="password"
                />
              </div>
            </div>
            
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <UserCog className="w-4 h-4" />
                Page Access Permissions
              </h4>
              <div className="space-y-3">
                {PERMISSIONS_CONFIG.map((perm) => (
                  <div key={perm.key} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{perm.label}</p>
                      <p className="text-xs text-muted-foreground">{perm.description}</p>
                    </div>
                    <Checkbox
                      checked={formData.permissions[perm.key]}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        permissions: { ...formData.permissions, [perm.key]: checked }
                      })}
                      data-testid={`perm-${perm.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleSubmit} disabled={isSubmitting} className="rounded-full" data-testid="save-admin-btn">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingAdmin ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
